import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLines } from "./Reveal";
import { scrollToId } from "./SmoothScroll";
import { CONTACT } from "../data";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.28]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.72, 0.95]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex flex-col justify-end"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src="https://images.pexels.com/photos/10696099/pexels-photo-10696099.jpeg"
          alt="Brisbane skyline at night"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-10 bg-obsidian"
      />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-obsidian via-obsidian/60 to-obsidian/80" />

      {/* Top meta bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 1 }}
        className="relative z-20 max-w-[1600px] mx-auto w-full px-6 lg:px-12 pt-28 flex justify-between text-xs uppercase tracking-[0.25em] text-zinc-400"
      >
        <span>Commercial · Industrial</span>
        <span className="hidden md:block">Est. Brisbane, AU</span>
      </motion.div>

      {/* Headline */}
      <motion.div
        style={{ y: textY }}
        className="relative z-20 max-w-[1600px] mx-auto w-full px-6 lg:px-12 pb-16 lg:pb-24"
      >
        <div className="mt-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="mb-6 text-xs uppercase tracking-[0.3em] text-acid font-bold flex items-center gap-3"
          >
            <span className="w-8 h-px bg-acid" />
            {CONTACT.regions.join(" · ")}
          </motion.p>

          <h1 className="font-display uppercase tracking-tighter text-white text-[16vw] sm:text-[13vw] lg:text-[10.5vw] leading-[0.85] font-semibold">
            <MaskedLines lines={["Pure Space,", "Elevated."]} start={0.25} />
          </h1>

          <div className="mt-10 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.8 }}
              className="max-w-md text-base lg:text-lg text-zinc-300 font-light leading-relaxed"
            >
              Premium commercial & industrial cleaning for offices and warehouses
              across Brisbane, North Brisbane and the Sunshine Coast.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.25, duration: 0.8 }}
              className="flex items-center gap-4"
            >
              <button
                data-testid="hero-quote-btn"
                onClick={() => scrollToId("quote")}
                className="group inline-flex items-center gap-3 bg-acid text-obsidian px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-white transition-colors duration-300"
              >
                Request a Quote
                <ArrowDown
                  size={16}
                  className="group-hover:translate-y-1 transition-transform"
                />
              </button>
              <button
                data-testid="hero-services-btn"
                onClick={() => scrollToId("services")}
                className="inline-flex items-center border border-white/25 text-white px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:border-acid hover:text-acid transition-colors duration-300"
              >
                Our Services
              </button>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 right-6 lg:right-12 z-20 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500"
      >
        <span>Scroll</span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
        >
          <ArrowDown size={14} className="text-acid" />
        </motion.span>
      </motion.div>
    </section>
  );
}
